import { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { routes, vendorRoutes, adminRoutes, VendorLayout, AdminLayout, Role } from './routes.config';
import { ProtectedRoute } from './ProtectedRoute';
import { GuestRoute } from './GuestRoute';
import { MainLayout } from '@shared/components/layout/MainLayout';
import { ErrorFallback } from '@shared/components/feedback/ErrorFallback';

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* Main routes */}
          {routes.map((route) => {
            const Element = route.element;
            let content = <Element />;

            // Wrap in ProtectedRoute if needed
            if (route.protected) {
              content = <ProtectedRoute requiredRoles={route.requiredRoles}>{content}</ProtectedRoute>;
            }

            // Wrap in GuestRoute if needed (for login, register, etc.)
            if (route.guestOnly) {
              content = <GuestRoute>{content}</GuestRoute>;
            }

            // Wrap in layout
            if (route.layout === 'main') {
              content = <MainLayout>{content}</MainLayout>;
            }

            return (
              <Route key={route.path} path={route.path} element={content} />
            );
          })}

          {/* Vendor routes */}
          <Route
            path="/vendor"
            element={
              <ProtectedRoute requiredRoles={[Role.VENDOR, Role.ADMIN]}>
                <Suspense fallback={<LoadingFallback />}>
                  <VendorLayout />
                </Suspense>
              </ProtectedRoute>
            }
          >
            {vendorRoutes.map((route) => {
              const Element = route.element;
              const relativePath = route.path.replace('/vendor', '') || '/';
              const indexPath = relativePath === '/' ? '' : relativePath.slice(1);
              return (
                <Route
                  key={route.path}
                  path={indexPath || undefined}
                  index={!indexPath}
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Element />
                    </Suspense>
                  }
                />
              );
            })}
          </Route>

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={[Role.ADMIN]}>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminLayout />
                </Suspense>
              </ProtectedRoute>
            }
          >
            {adminRoutes.map((route) => {
              const Element = route.element;
              const relativePath = route.path.replace('/admin', '') || '/';
              const indexPath = relativePath === '/' ? '' : relativePath.slice(1);
              return (
                <Route
                  key={route.path}
                  path={indexPath || undefined}
                  index={!indexPath}
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Element />
                    </Suspense>
                  }
                />
              );
            })}
          </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
